import { api } from '@goaltree/backend/convex/_generated/api'
import { Button } from '@goaltree/ui/components/button'
import { createFileRoute } from '@tanstack/react-router'
import { useConvexAuth, useQuery } from 'convex/react'
import { Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { OAuthCard } from '@/components/oauth-card'
import { authClient } from '@/lib/auth-client'

// Consent for dynamically registered MCP clients (ChatGPT, Claude via DCR,
// Cursor…). better-auth's oidc-provider redirects here with a consent_code.
export const Route = createFileRoute('/oauth/consent')({
  head: () => ({ meta: [{ title: 'Allow access — Goaltree' }] }),
  validateSearch: (s): { consent_code?: string; client_id?: string } => ({
    consent_code: typeof s.consent_code === 'string' ? s.consent_code : undefined,
    client_id: typeof s.client_id === 'string' ? s.client_id : undefined,
  }),
  component: ConsentPage,
})

function ConsentPage() {
  const { t } = useTranslation()
  const { consent_code, client_id } = Route.useSearch()
  const { isAuthenticated, isLoading } = useConvexAuth()
  const client = useQuery(
    api.mcpTokens.getOAuthClientInfo,
    client_id ? { clientId: client_id } : 'skip',
  )
  const me = useQuery(api.auth.getCurrentUser, isAuthenticated ? {} : 'skip')
  const [busy, setBusy] = useState<'allow' | 'deny' | null>(null)
  const [failed, setFailed] = useState(false)

  async function answer(accept: boolean) {
    setBusy(accept ? 'allow' : 'deny')
    try {
      const { data } = await authClient.$fetch<{ redirectURI?: string }>('/oauth2/consent', {
        method: 'POST',
        body: { accept, consent_code },
      })
      if (data?.redirectURI) window.location.replace(data.redirectURI)
      else setFailed(true)
    } catch {
      setFailed(true)
    } finally {
      setBusy(null)
    }
  }

  if (!consent_code || !client_id || failed || client === null || (!isLoading && !isAuthenticated))
    return (
      <OAuthCard>
        <p className="text-sm text-muted-foreground">{t('oauth.error')}</p>
      </OAuthCard>
    )
  if (client === undefined || isLoading)
    return (
      <OAuthCard>
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </OAuthCard>
    )

  return (
    <OAuthCard>
      <div className="space-y-2">
        <h1 className="text-lg font-semibold leading-snug">
          {t('oauth.title', { name: client.name })}
        </h1>
        <p className="text-sm text-muted-foreground">{t('oauth.desc')}</p>
        {client.redirectHost && (
          <p className="text-xs text-muted-foreground">
            {t('oauth.redirectNote', { host: client.redirectHost })}
          </p>
        )}
        {me?.email && (
          <p className="text-xs text-muted-foreground">
            {t('oauth.signedInAs', { email: me.email })}
          </p>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          disabled={!!busy}
          onClick={() => answer(false)}
        >
          {t('oauth.deny')}
        </Button>
        <Button className="flex-1" disabled={!!busy} onClick={() => answer(true)}>
          {busy === 'allow' ? <Loader2 className="h-4 w-4 animate-spin" /> : t('oauth.allow')}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">{t('oauth.revokeHint')}</p>
    </OAuthCard>
  )
}
